// Booking window helpers shared by the server routes and the client calendar.
//
// Convention: slot and appointment datetimes are tenant wall-clock values stored
// as UTC and never converted. A JS Date built from one therefore carries the
// stored digits in its UTC fields, so day keys always come from the UTC getters
// (or the ISO string's date part), never from local getters, on any host.
//
// "Today" is the only place a real clock enters: the cutoff is the current UTC
// calendar day plus N days, inclusive, on server and client alike. Using the
// UTC date keeps both sides host-independent; the window simply advances a day
// when the UTC date rolls over, which for US tenants is in the evening.

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/

// 0 means no limit. Anything that is not a positive finite number is ignored
// with a warning so a typo in NUXT_PUBLIC_BOOKING_WINDOW_DAYS does not silently
// disable the window.
export function normalizeBookingWindowDays(raw: unknown): number {
  if (raw === undefined || raw === null || raw === '' || raw === 0) return 0
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
  if (typeof raw === 'boolean' || !Number.isFinite(n) || n < 0) {
    console.warn(
      `[bookingWindow] ignoring invalid NUXT_PUBLIC_BOOKING_WINDOW_DAYS value ${JSON.stringify(raw)}; no booking window applied`
    )
    return 0
  }
  return Math.floor(n)
}

// 'YYYY-MM-DD' of a slot's TimeSlotStart (Date or ISO string) or a date key.
export function slotDateKey(value: Date | string | null | undefined): string {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().split('T')[0]
  }
  return String(value ?? '').split('T')[0]
}

// Last bookable day as 'YYYY-MM-DD' (UTC today + days), or null when there is
// no limit.
export function bookingWindowEndKey(days: number, now: Date = new Date()): string | null {
  if (!days) return null
  const end = new Date(now)
  end.setUTCDate(end.getUTCDate() + days)
  return slotDateKey(end)
}

export function isWithinBookingWindow(
  slotStartOrKey: Date | string | null | undefined,
  endKey: string | null
): boolean {
  if (!endKey) return true
  const key = slotDateKey(slotStartOrKey)
  return DAY_KEY.test(key) && key <= endKey
}

export function isDayKey(value: unknown): boolean {
  return DAY_KEY.test(String(value ?? ''))
}

// 'YYYY-MM-DD' -> local-midnight Date, for comparing against the datepicker's
// local Date values.
export function dayKeyToLocalDate(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}
